/* [EZ] Slide cart — <ez-cart-drawer> custom element.
 * Opens from the right on add-to-cart, store-wide:
 *   • our `ez:cart:added`  (collection / homepage cards via ez-product-card)  → always open
 *   • Horizon `cart:update` (PDP + any native add-to-cart)                    → open if count rose
 * Renders items + totals from /cart.js; qty/remove via /cart/change.js. */
(function () {
  'use strict';

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function sized(url, w) { if (!url) return ''; return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }

  // Shopify money formatter (uses shop.money_format passed from liquid)
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var ph = /\{\{\s*(\w+)\s*\}\}/, fmt = format || '${{amount}}';
    function withDelims(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision; thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }
    var value = '0', m = fmt.match(ph);
    switch (m ? m[1] : 'amount') {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
    }
    return fmt.replace(ph, value);
  }

  var TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var EZCart = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.lastCount = null;
      this.busy = false;
      this.els = {
        backdrop: this.querySelector('.ez-cart__backdrop'),
        panel: this.querySelector('.ez-cart__panel'),
        items: this.querySelector('[data-ez-cart-items]'),
        empty: this.querySelector('[data-ez-cart-empty]'),
        foot: this.querySelector('[data-ez-cart-foot]'),
        countLabel: this.querySelector('[data-ez-cart-count-label]'),
        subtotal: this.querySelector('[data-ez-cart-subtotal]'),
        discount: this.querySelector('[data-ez-cart-discount]'),
        savings: this.querySelector('[data-ez-cart-savings]'),
        total: this.querySelector('[data-ez-cart-total]'),
      };

      // close handlers
      this.querySelectorAll('[data-ez-cart-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });

      // item interactions (delegated)
      this.els.items.addEventListener('click', (e) => {
        if (this.busy) return; // ignore taps while a remove/update is in flight
        var row = e.target.closest('[data-key]'); if (!row) return;
        var key = row.getAttribute('data-key');
        if (e.target.closest('[data-ez-remove]')) {
          // mark the whole drawer busy (disables buttons + shows the updating overlay), slide the
          // row out to the right, then remove it.
          this.setBusy(true);
          row.classList.add('is-removing');
          var self = this, done = false;
          var go = function () { if (done) return; done = true; self.change(key, 0); };
          row.addEventListener('transitionend', go, { once: true });
          setTimeout(go, 420); // fallback if transitionend doesn't fire
          return;
        }
        var step = e.target.closest('[data-ez-qty]');
        if (step) { this.setBusy(true); var d = step.getAttribute('data-ez-qty') === 'inc' ? 1 : -1; this.change(key, Math.max(0, (parseInt(row.getAttribute('data-qty'), 10) || 1) + d)); }
      });

      // open triggers
      document.querySelectorAll('[data-ez-cart-open]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); this.refresh().then(() => this.open()); }));

      // add-to-cart triggers (store-wide)
      document.addEventListener('ez:cart:added', () => this.refresh().then(() => this.open()));
      document.addEventListener('cart:update', () => {
        var prev = this.lastCount;
        this.refresh().then((cart) => { if (cart && prev != null && cart.item_count > prev) this.open(); });
      });

      // initial count
      this.refresh();
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.els.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ez-cart-open');
      document.body.style.overflow = 'hidden';
      var c = this.els.panel.querySelector('.ez-cart__close'); if (c) c.focus();
    }
    close() {
      this.classList.remove('is-open');
      this.els.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ez-cart-open');
      document.body.style.overflow = '';
    }

    refresh() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => { this.render(cart); return cart; })
        .catch(() => null);
    }

    setBusy(on) {
      this.busy = on;
      this.classList.toggle('is-busy', on);
      var ld = this.querySelector('.ez-cart__loading');
      if (ld) ld.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    change(key, qty) {
      this.setBusy(true); // keep the drawer locked through the network round-trip + re-render
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      })
        .then((r) => r.json())
        .then((cart) => { this.render(cart); })
        .catch(() => {})
        .finally(() => { this.setBusy(false); });
    }

    money(cents) { return formatMoney(cents, this.moneyFormat); }

    render(cart) {
      if (!cart || !cart.items) return;
      this.lastCount = cart.item_count;
      // header cart count badge(s)
      document.querySelectorAll('[data-ez-cart-count]').forEach((b) => { b.textContent = cart.item_count; b.hidden = cart.item_count === 0; });
      if (this.els.countLabel) this.els.countLabel.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');

      if (!cart.items.length) {
        this.els.items.innerHTML = '';
        this.els.empty.hidden = false;
        this.els.foot.hidden = true;
        return;
      }
      this.els.empty.hidden = true;
      this.els.foot.hidden = false;

      this.els.items.innerHTML = cart.items.map((it) => {
        var variant = it.variant_title && it.variant_title !== 'Default Title' ? '<div class="ez-cart__variant">' + esc(it.variant_title) + '</div>' : '';
        return '<li class="ez-cart__item" data-key="' + esc(it.key) + '" data-qty="' + it.quantity + '">'
          + '<a class="ez-cart__thumb" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(sized(it.image, 128)) + '" alt="' + esc(it.product_title) + '" loading="lazy">' : '') + '</a>'
          + '<div class="ez-cart__info">'
          + '<a class="ez-cart__name" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>'
          + variant
          + '<div class="ez-cart__qty"><button type="button" data-ez-qty="dec" aria-label="Decrease quantity">&minus;</button><span>' + it.quantity + '</span><button type="button" data-ez-qty="inc" aria-label="Increase quantity">+</button></div>'
          + '<div class="ez-cart__price">' + this.money(it.final_line_price) + '</div>'
          + '</div>'
          + '<button type="button" class="ez-cart__remove" data-ez-remove aria-label="Remove ' + esc(it.product_title) + '">' + TRASH + '</button>'
          + '</li>';
      }).join('');

      // totals
      this.els.subtotal.textContent = this.money(cart.items_subtotal_price);
      this.els.discount.textContent = this.money(cart.cart_level_discount_applications ? cart.cart_level_discount_applications.reduce((n, d) => n + (d.total_allocated_amount || 0), 0) : 0);
      this.els.savings.textContent = this.money(cart.total_discount);
      this.els.total.textContent = this.money(cart.total_price);
    }
  };

  if (!customElements.get('ez-cart-drawer')) customElements.define('ez-cart-drawer', EZCart);

  // Global Add-to-Cart: any [data-ez-cart-add] form on any page (collection cards, top picks, …)
  // → AJAX /cart/add.js → fires ez:cart:added (the drawer above opens on it).
  document.addEventListener('submit', function (ev) {
    var form = ev.target.closest && ev.target.closest('[data-ez-cart-add]');
    if (!form) return;
    ev.preventDefault();
    var btn = form.querySelector('[data-atc]');
    var input = form.querySelector('input[name="id"]');
    var id = input && input.value;
    if (!id) return;
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    }).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        if (!res.ok) { console.warn('[ez] add to cart failed', res.data); return; }
        document.dispatchEvent(new CustomEvent('ez:cart:added', { detail: res.data }));
        if (btn) {
          var label = btn.getAttribute('data-label') || btn.textContent;
          btn.setAttribute('data-label', label);
          btn.classList.add('is-added'); btn.textContent = 'Added ✓';
          setTimeout(function () { btn.classList.remove('is-added'); btn.textContent = label; }, 1600);
        }
      }).catch(function (e) {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        console.warn('[ez] add to cart error', e);
      });
  });

  // Global Buy Now: any [data-ez-buy-now="<variant id>"] element (collection cards,
  // [EZ] Columns, PDP) → clear the cart, add JUST this variant, go straight to /checkout
  // (so checkout contains that product only). Falls back to the element's href if JS fails.
  document.addEventListener('click', function (ev) {
    var el = ev.target.closest && ev.target.closest('[data-ez-buy-now]');
    if (!el) return;
    ev.preventDefault();
    var id = el.getAttribute('data-ez-buy-now');
    var qty = 1;
    // Form-aware (PDP [EZ] Buy Now block): read the LIVE variant + quantity from the product
    // form at click time, so option changes / quantity are respected. Falls back to the static
    // data-ez-buy-now id (collection cards, [EZ] Columns — no variant picker there).
    if (el.hasAttribute('data-ez-buy-now-form')) {
      var form = el.closest('product-form-component, form') ||
                 document.querySelector('form[data-type="add-to-cart-form"]');
      var idInput = form && form.querySelector('[name="id"]');
      var qtyInput = form && form.querySelector('[name="quantity"]');
      if (idInput && idInput.value) id = idInput.value;
      if (qtyInput && qtyInput.value) { var q = parseInt(qtyInput.value, 10); if (q > 0) qty = q; }
    }
    if (!id || el.classList.contains('is-loading')) return;
    setBuyNowLoading(el, true);
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: qty })
        });
      })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) { setBuyNowLoading(el, false); console.warn('[ez] buy now failed', res.data); return; }
        window.location.href = '/checkout';
      })
      .catch(function (e) { setBuyNowLoading(el, false); console.warn('[ez] buy now error', e); });
  });

  // Buy Now loading visual: the caption is removed and ONLY the icon is shown, doing a success-
  // style pop animation (like the sticky add-to-cart) until /checkout opens. Restorable for the
  // browser-back reset (original markup stashed on the element). Works on the PDP [EZ] Buy Now
  // (which already has an icon) and on collection/columns Buy Now (no icon → fall back to a bolt).
  var BOLT_SVG = '<svg class="ez-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false"><path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z"/></svg>';
  function setBuyNowLoading(el, on) {
    if (on) {
      if (!el.dataset.ezOrig) el.dataset.ezOrig = el.innerHTML;
      el.classList.add('is-loading');
      el.setAttribute('aria-busy', 'true');
      var existing = el.querySelector('.ez-buynow-block__icon');
      var iconHTML = existing ? existing.outerHTML : '<span class="ez-buynow-block__icon">' + BOLT_SVG + '</span>';
      el.innerHTML = '<span class="ez-buynow-pop">' + iconHTML + '</span>';
    } else {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
      if (el.dataset.ezOrig != null) { el.innerHTML = el.dataset.ezOrig; delete el.dataset.ezOrig; }
    }
  }
  // expose for the PDP reset handler
  window.ezSetBuyNowLoading = setBuyNowLoading;
})();
